import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Paper,
  Typography,
  TableCell,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  Button,
  Box,
  TableSortLabel,
  Card,
  CardContent,
  CardHeader,
  Grid,
  useMediaQuery,
  tableCellClasses,
  Skeleton,
  Select,
  MenuItem,
  Divider,
  TextField,
  IconButton,
  Collapse,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";
import { useAppStore } from "../../appStore";
import { api_url } from "../../apiUtils";
import * as XLSX from "xlsx";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#e6e8fc",
    color: "#1e2466",
    border: "1px solid #bdbdbd !important",
    fontWeight: "bold",
    letterSpacing: 0.5,
    textAlign: "center",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    border: "1px solid #bdbdbd !important",
    padding: 8,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#F7F9FB",
  },
  "&:last-child td, &:last-child th": {
    borderBottom: 0,
  },
}));

const commonIconStyles = {
  fontSize: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  border: "2px solid",
  marginRight: "5px",
};

const commonChipStyle = {
  width: "90px",
  height: "25px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "20px",
  fontSize: "12px",
  color: "#fff",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  textTransform: "capitalize",
};

const WalletStatements = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const { selectedDateHistory, setSelectedDateHistory } = useAppStore();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setFilterOpen] = useState(false);

  const getData = async () => {
    setLoading(true);
    try {
      const url = selectedDateHistory
        ? `${api_url}wallet/user-statements?date=${selectedDateHistory}`
        : `${api_url}wallet/user-statements`;

      const response = await axios.get(url, {
        headers: { accesstoken: sessionStorage.getItem("accesstoken") },
      });
      const finalReport = response.data.statements;
      setTimeout(() => {
        setData(finalReport);
        setLoading(false);
      }, 1000);
    } catch (err) {
      console.log("Error fetching data:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [selectedDateHistory]);

  useEffect(() => {
    setSelectedDateHistory("");
    setData([]);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterType = (type) => {
    setSelectedType(type);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleSortRequest = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredData = data
    .filter((statement) =>
      selectedType ? statement.type === selectedType : true,
    )
    .filter((statement) =>
      selectedStatus ? statement.status === selectedStatus : true,
    )
    .filter((statement) =>
      searchQuery ? statement.transactionId === searchQuery : true,
    );
  console.log("searchquery", searchQuery);
  const sortedData = filteredData
    .filter((i) => i.type !== "revert")
    .sort((a, b) => {
      if (sortOrder === "asc") {
        return new Date(a.createdAt) - new Date(b.createdAt);
      } else {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  const handleCopyTransactionId = (transactionId) => {
    navigator.clipboard.writeText(transactionId);
  };

  // Determine if we should slice or show all
  const displayData = selectedDateHistory ? sortedData : sortedData.slice(0, 5);

  const getStatementSign = (statement) => {
    if (statement.type === "OTP") return "-";
    if (statement.transferType === "plus") return "+";
    if (statement.transferType === "minus") return "-";
    if (statement.type === "withdraw" && statement.status === "rejected") {
      return "+";
    }
    if (statement.type === "withdraw" || statement.type === "revert") {
      return "-";
    }
    if (statement.type === "deposit" && statement.status === "completed") {
      return "+";
    }
    return "";
  };

  const getStatementAmountColor = (statement) => {
    const sign = getStatementSign(statement);
    if (sign === "-") return "#dc2626";
    if (sign === "+") return "#15803d";
    if (statement.type === "deposit" && statement.status === "requested") {
      return "#1782FF";
    }
    return "inherit";
  };

  const exportToExcel = (array) => {
    const ws = XLSX.utils.json_to_sheet(
      array.map((item) => ({
        Type: item.type,
        "Previous Balance": item.previousBalance.toFixed(2),
        "Amount (Credit / Debit)": `${getStatementSign(item)}${Math.abs(item.amount)}`,
        "Net Balance": item.netBalance.toFixed(2),
        "Date & Time": new Date(item.createdAt).toLocaleString(),
        Status: item.status,
      })),
    );

    // Add styling
    const wscols = [
      { wch: 10 },
      { wch: 20 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
    ];
    ws["!cols"] = wscols;
    ws["!rows"] = [{ hpx: 20 }];

    // Header style
    const headerStyle = {
      fill: { fgColor: { rgb: "#FFFF00" } },
      font: { color: { rgb: "#FFFFFF" }, bold: true },
      alignment: { horizontal: "center", vertical: "center" },
    };

    ws["A1"].s = headerStyle;
    ws["B1"].s = headerStyle;
    ws["C1"].s = headerStyle;
    ws["D1"].s = headerStyle;
    ws["E1"].s = headerStyle;
    ws["F1"].s = headerStyle;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Wallet Statements");
    XLSX.writeFile(
      wb,
      `wallet_statements_${
        selectedDateHistory ? selectedDateHistory : Date.now()
      }.xlsx`,
    );
  };

  const downloadMobileStatement = (array) => {
    const pageWidth = 1240;
    const pageHeight = 1754;
    const rowsPerPage = 13;
    const pages = [];
    const statementPages = Array.from(
      { length: Math.max(1, Math.ceil(array.length / rowsPerPage)) },
      (_, index) => array.slice(index * rowsPerPage, (index + 1) * rowsPerPage),
    );

    statementPages.forEach((statementRows, pageIndex) => {
      const canvas = document.createElement("canvas");
      canvas.width = pageWidth;
      canvas.height = pageHeight;
      const context = canvas.getContext("2d");
      const margin = 64;
      const columns = [
        { label: "TRANSACTION", x: margin, width: 470 },
        { label: "CREDIT / DEBIT", x: 545, width: 205 },
        { label: "NET BALANCE", x: 770, width: 190 },
        { label: "DATE & TIME", x: 980, width: 196 },
      ];

      context.fillStyle = "#f4f6fb";
      context.fillRect(0, 0, pageWidth, pageHeight);
      context.fillStyle = "#ffffff";
      context.fillRect(32, 32, pageWidth - 64, pageHeight - 64);

      const gradient = context.createLinearGradient(32, 32, pageWidth - 32, 220);
      gradient.addColorStop(0, "#121a4e");
      gradient.addColorStop(1, "#3852ad");
      context.fillStyle = gradient;
      context.fillRect(32, 32, pageWidth - 64, 210);
      context.fillStyle = "#ffffff";
      context.font = "700 45px Arial";
      context.fillText("SM INDIA 2050", margin, 112);
      context.font = "700 34px Arial";
      context.fillText("Wallet Statement", margin, 165);
      context.fillStyle = "#dce4ff";
      context.font = "24px Arial";
      context.fillText(`Generated ${new Date().toLocaleString("en-IN")}`, margin, 207);
      context.textAlign = "right";
      context.fillText(`Page ${pageIndex + 1} of ${statementPages.length}`, pageWidth - margin, 207);
      context.textAlign = "left";

      let y = 294;
      context.fillStyle = "#e9edff";
      context.fillRect(margin, y, pageWidth - margin * 2, 64);
      context.fillStyle = "#16205c";
      context.font = "700 20px Arial";
      columns.forEach((column) => context.fillText(column.label, column.x + 16, y + 40));
      y += 64;

      if (!statementRows.length) {
        context.fillStyle = "#65708d";
        context.font = "28px Arial";
        context.fillText("No transactions found.", margin + 16, y + 55);
      }

      statementRows.forEach((item, rowIndex) => {
        const rowHeight = 94;
        const sign = getStatementSign(item);
        const amountColor = sign === "+" ? "#15803d" : "#dc2626";
        context.fillStyle = rowIndex % 2 === 0 ? "#ffffff" : "#fafbff";
        context.fillRect(margin, y, pageWidth - margin * 2, rowHeight);
        context.strokeStyle = "#e5e9f3";
        context.beginPath();
        context.moveTo(margin, y + rowHeight);
        context.lineTo(pageWidth - margin, y + rowHeight);
        context.stroke();

        context.fillStyle = "#182046";
        context.font = "700 23px Arial";
        context.fillText(getStatementDisplayType(item).slice(0, 31), margin + 16, y + 37);
        context.fillStyle = "#78819a";
        context.font = "18px Arial";
        context.fillText((item.transactionId || "No transaction ID").slice(0, 38), margin + 16, y + 68);
        context.fillStyle = amountColor;
        context.font = "700 25px Arial";
        context.fillText(`${sign}${Math.abs(item.amount).toFixed(2)}`, columns[1].x + 16, y + 53);
        context.fillStyle = "#182046";
        context.font = "23px Arial";
        context.fillText(Number(item.netBalance || 0).toFixed(2), columns[2].x + 16, y + 53);
        context.font = "19px Arial";
        context.fillText(new Date(item.createdAt).toLocaleString("en-IN").slice(0, 21), columns[3].x + 16, y + 53);
        y += rowHeight;
      });

      context.fillStyle = "#77809a";
      context.font = "18px Arial";
      context.textAlign = "center";
      context.fillText("This is a computer-generated wallet statement.", pageWidth / 2, pageHeight - 72);
      context.textAlign = "left";
      pages.push(canvas.toDataURL("image/jpeg", 0.92));
    });

    const textEncoder = new TextEncoder();
    const toBytes = (value) => textEncoder.encode(value);
    const base64ToBytes = (base64) => {
      const binary = window.atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
      return bytes;
    };
    const chunks = [];
    const offsets = [0];
    let size = 0;
    const append = (value) => {
      const bytes = typeof value === "string" ? toBytes(value) : value;
      chunks.push(bytes);
      size += bytes.length;
    };
    const addObject = (objectNumber, content) => {
      offsets[objectNumber] = size;
      append(`${objectNumber} 0 obj\n`);
      append(content);
      append("\nendobj\n");
    };

    append("%PDF-1.4\n%");
    append(new Uint8Array([0xe2, 0xe3, 0xcf, 0xd3]));
    append("\n");
    addObject(1, "<< /Type /Catalog /Pages 2 0 R >>");
    const pageObjectNumbers = pages.map((_, index) => 5 + index * 3);
    addObject(2, `<< /Type /Pages /Count ${pages.length} /Kids [${pageObjectNumbers.map((number) => `${number} 0 R`).join(" ")}] >>`);

    pages.forEach((imageData, index) => {
      const objectBase = 3 + index * 3;
      const imageBytes = base64ToBytes(imageData.split(",")[1]);
      offsets[objectBase] = size;
      append(`${objectBase} 0 obj\n<< /Type /XObject /Subtype /Image /Width ${pageWidth} /Height ${pageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${imageBytes.length} >>\nstream\n`);
      append(imageBytes);
      append("\nendstream\nendobj\n");
      const contents = `q\n${pageWidth} 0 0 ${pageHeight} 0 0 cm\n/StatementImage Do\nQ`;
      addObject(objectBase + 1, `<< /Length ${toBytes(contents).length} >>\nstream\n${contents}\nendstream`);
      addObject(objectBase + 2, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /StatementImage ${objectBase} 0 R >> >> /Contents ${objectBase + 1} 0 R >>`);
    });

    const xrefOffset = size;
    append(`xref\n0 ${offsets.length}\n0000000000 65535 f \n`);
    for (let index = 1; index < offsets.length; index += 1) {
      append(`${String(offsets[index]).padStart(10, "0")} 00000 n \n`);
    }
    append(`trailer\n<< /Size ${offsets.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

    const blob = new Blob(chunks, { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `wallet_statement_${selectedDateHistory || Date.now()}.pdf`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const getAmountSuffix = (statement) => {
    if (
      statement.type === "transfer" &&
      statement.transactionId?.startsWith("OTF")
    ) {
      return "[CD]";
    }
    if (statement.type === "bonus") {
      return "[CC]";
    }
    if (statement.type === "HWLDeduction") {
      return "[HWL]";
    }
    return "";
  };

  const formatStatementAmount = (statement) => {
    const sign = getStatementSign(statement);

    const suffix = getAmountSuffix(statement);
    return `${sign}${Math.abs(statement.amount)} ${suffix}`.trim();
  };

  const getStatementDisplayType = (statement) => {
    if (statement?.title) return statement.title;
    return statement?.type || "Unknown";
  };

  const getTypeStyles = (type) => {
    const bgColor =
      (type === "deposit" && "#4caf50") ||
      (type === "withdraw" && "#ef5350") ||
      (type === "revert" && "#ffc107") ||
      (type === "transfer" && "#607d8b") ||
      (type === "bonus" && "#388e3c") ||
      (type === "HWLDeduction" && "#e53935") ||
      (type === "OTP" && "#e53935");

    return {
      ...commonChipStyle,
      bgcolor: bgColor,
      "&:hover": {
        bgcolor: bgColor,
      },
    };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return {
          icon: (
            <CheckIcon
              sx={{
                ...commonIconStyles,
                borderColor: "#388e3c",
                color: "#388e3c",
              }}
            />
          ),
          color: "#388e3c",
        };
      case "requested":
        return {
          icon: (
            <ArrowOutwardIcon
              sx={{
                ...commonIconStyles,
                borderColor: "#1782FF",
                color: "#1782FF",
              }}
            />
          ),
          color: "#1782FF",
        };
      case "rejected":
        return {
          icon: (
            <CloseIcon
              sx={{
                ...commonIconStyles,
                borderColor: "#ff3d00",
                color: "#ff3d00",
              }}
            />
          ),
          color: "#ff3d00",
        };
      case "verifying":
        return {
          icon: (
            <QuestionMarkIcon
              sx={{
                ...commonIconStyles,
                borderColor: "#ffc107",
                color: "#ffc107",
              }}
            />
          ),
          color: "#ffc107",
        };
      default:
        return { icon: null, color: "default" };
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {/* {isMobile ? "" : <Typography>Filter Statements:</Typography>} */}

      {isMobile ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setFilterOpen(!isFilterOpen)}
              startIcon={<FilterAltIcon />}
              sx={{ mb: 2 }}
              color="warning"
            >
              Filter
            </Button>
          </Box>

          <Collapse in={isFilterOpen}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Box
                  sx={{
                    display: "flex",
                    // flexWrap: "wrap",
                    gap: "2px",
                    alignItems: "center",
                  }}
                >
                  <Chip
                    label="All"
                    onClick={() => handleFilterType("")}
                    sx={{
                      ...commonChipStyle,
                      bgcolor: selectedType === "" ? "#1782FF" : "#f0f0f0",
                      cursor: "pointer",
                    }}
                  />
                  {["deposit", "withdraw", "revert", "transfer"].map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      onClick={() => handleFilterType(type)}
                      sx={{
                        ...commonChipStyle,
                        bgcolor:
                          selectedType === type
                            ? getTypeStyles(type).bgcolor
                            : "#bdbdbd",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor:
                            selectedType === type
                              ? getTypeStyles(type).bgcolor
                              : "#a3a3a3",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sm={8}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "2px",
                    alignItems: "center",
                  }}
                >
                  <Select
                    size="small"
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Status" }}
                    sx={{ width: "100%", maxWidth: 200 }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <Divider sx={{ m: "2px 4px 0px 4px !important" }} />

                    <MenuItem
                      value="completed"
                      sx={{ color: getStatusIcon("completed").color }}
                    >
                      Completed
                    </MenuItem>
                    <Divider sx={{ m: "2px 4px 0px 4px !important" }} />
                    <MenuItem
                      value="requested"
                      sx={{ color: getStatusIcon("requested").color }}
                    >
                      Requested
                    </MenuItem>
                    <Divider sx={{ m: "2px 4px 0px 4px !important" }} />

                    <MenuItem
                      value="rejected"
                      sx={{ color: getStatusIcon("rejected").color }}
                    >
                      Rejected
                    </MenuItem>
                    <Divider sx={{ m: "2px 4px 0px 4px !important" }} />

                    <MenuItem
                      value="verifying"
                      sx={{ color: getStatusIcon("verifying").color }}
                    >
                      Verifying
                    </MenuItem>
                  </Select>
                </Box>

                {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">Search by Transaction ID:</Typography>
              <TextField
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter Transaction ID"
                sx={{ width: "100%", maxWidth: 300 }}
              />
            </Box> */}

                <Button
                  variant={{ xs: "outlined", xl: "contained" }}
                  sx={{
                    // width: { xs: "100%", sm: "auto" },
                    // px: 3,
                    textTransform: "capitalize",
                    bgcolor: isMobile ? "" : "#2e7d32",
                    color: isMobile ? "#ef6c00" : "#ffffff",
                    letterSpacing: 1,
                    border: "1px solid #ff9800",
                    whiteSpace: "nowrap",
                    fontSize: "0.8rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() =>
                    isMobile
                      ? downloadMobileStatement(filteredData)
                      : exportToExcel(filteredData)
                  }
                >
                  {isMobile ? "Download Statement" : "Export .xlsx"}
                  <DownloadIcon sx={{ fontSize: "16px", ml: 1 }} />
                </Button>
              </Grid>
              {/* <Grid item xs={12} sm={6}></Grid> */}
            </Grid>
          </Collapse>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setFilterOpen(!isFilterOpen)}
              startIcon={<FilterAltIcon />}
              sx={{ mb: 2 }}
              color="warning"
            >
              Filter
            </Button>
          </Box>
          <Collapse in={isFilterOpen}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} xl={4}>
                <Box
                  sx={{
                    display: "flex",
                    // flexWrap: "wrap",
                    gap: "2px",
                    alignItems: "center",
                  }}
                >
                  <Chip
                    label="All"
                    onClick={() => handleFilterType("")}
                    sx={{
                      ...commonChipStyle,
                      bgcolor: selectedType === "" ? "#1782FF" : "#f0f0f0",
                      cursor: "pointer",
                    }}
                  />
                  {["deposit", "withdraw", "revert", "transfer"].map((type) => (
                    <Chip
                      key={type}
                      label={type}
                      onClick={() => handleFilterType(type)}
                      sx={{
                        ...commonChipStyle,
                        bgcolor:
                          selectedType === type
                            ? getTypeStyles(type).bgcolor
                            : "#bdbdbd",
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor:
                            selectedType === type
                              ? getTypeStyles(type).bgcolor
                              : "#a3a3a3",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                xl={8}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: "2px",
                    alignItems: "center",
                  }}
                >
                  <Select
                    size="small"
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Status" }}
                    sx={{ width: "100%", maxWidth: 200 }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <Divider sx={{ m: "2px 4px 0px 4px !important" }} />

                    <MenuItem
                      value="completed"
                      sx={{ color: getStatusIcon("completed").color }}
                    >
                      Completed
                    </MenuItem>
                    <Divider sx={{ m: "2px 4px 0px 4px !important" }} />
                    <MenuItem
                      value="requested"
                      sx={{ color: getStatusIcon("requested").color }}
                    >
                      Requested
                    </MenuItem>
                    <Divider sx={{ m: "2px 4px 0px 4px !important" }} />

                    <MenuItem
                      value="rejected"
                      sx={{ color: getStatusIcon("rejected").color }}
                    >
                      Rejected
                    </MenuItem>
                    <Divider sx={{ m: "2px 4px 0px 4px !important" }} />

                    <MenuItem
                      value="verifying"
                      sx={{ color: getStatusIcon("verifying").color }}
                    >
                      Verifying
                    </MenuItem>
                  </Select>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
                  <Typography variant="body2">
                    Search by Transaction ID:
                  </Typography>
                  <TextField
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Enter Transaction ID"
                    sx={{ width: "100%", maxWidth: 300 }}
                  />
                </Box>

                <Button
                  variant={{ xs: "outlined", xl: "contained" }}
                  sx={{
                    // width: { xs: "100%", sm: "auto" },
                    // px: 3,
                    textTransform: "capitalize",
                    bgcolor: isMobile ? "" : "#2e7d32",
                    color: isMobile ? "#ef6c00" : "#ffffff",
                    letterSpacing: 1,
                    whiteSpace: "nowrap",
                    fontSize: "0.8rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() =>
                    isMobile
                      ? downloadMobileStatement(filteredData)
                      : exportToExcel(filteredData)
                  }
                >
                  {isMobile ? "Download Statement" : "Export .xlsx"}
                  <DownloadIcon sx={{ fontSize: "16px", ml: 1 }} />
                </Button>
              </Grid>
              {/* <Grid item xs={12} sm={6}></Grid> */}
            </Grid>
          </Collapse>
        </>
      )}

      {loading ? (
        isMobile ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: 2,
            }}
          >
            {Array.from({ length: 5 }).map((_, index) => (
              <Card
                key={index}
                sx={{ width: "100%", mb: 2, mt: { xs: 2, sm: 0 } }}
              >
                <CardHeader
                  title={<Skeleton variant="text" width="40%" />}
                  subheader={<Skeleton variant="text" width="60%" />}
                />
                <CardContent>
                  <Skeleton variant="rectangular" height={60} />
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100px",
            }}
          >
            <CircularProgress />
          </div>
        )
      ) : (
        <>
          {isMobile ? (
            // Mobile view using cards
            <Box sx={{ mt: 2 }}>
              {displayData?.length === 0 && (
                <Card sx={{ width: "100%", mb: 2 }}>
                  <CardContent
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ color: "red" }}>
                      No Data Found.... 😔
                    </Typography>
                  </CardContent>
                </Card>
              )}
              {/* <Grid
                item
                xs={12}
                sm={6}
                md={4}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">
                    Search by Transaction ID:
                  </Typography>
                  <TextField
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Enter Transaction ID"
                    sx={{ width: "100%", maxWidth: 300 }}
                  />
                </Box>
              </Grid> */}

              {displayData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((statement) => {
                  const { icon, color } = getStatusIcon(statement.status);
                  return (
                    <Card
                      key={statement._id}
                      sx={{ mb: 1, mt: { xs: 2, sm: 0 } }}
                    >
                      <CardHeader
                        title={
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span>{getStatementDisplayType(statement)} </span>
                            <span
                              style={{
                                fontSize: "0.7rem", // Smaller font size
                                color: "#757575", // Red color
                                fontWeight: "normal !important",
                              }}
                            >
                              #{statement.transactionId}
                            </span>
                          </div>
                        }
                        subheader={`Date: ${new Date(
                          statement.createdAt,
                        ).toLocaleString()}`}
                        sx={{
                          // color: getTypeStyles(statement.type).bgcolor,
                          bgcolor: "#e6e8fc",
                          color:
                            statement.type === "withdraw" ||
                            statement.type === "revert" ||
                            statement.status === "rejected"
                              ? "#f44336"
                              : statement.type === "deposit" &&
                                  statement.status === "completed"
                                ? "green"
                                : statement.status === "requested" &&
                                    statement.type === "deposit"
                                  ? "#42a5f5"
                                  : "",
                          // color: "#fff", // This applies color to both title and subheader
                          "& .MuiCardHeader-title": {
                            // color: "#fff", // Ensures title is white
                            letterSpacing: 0.5,
                            fontSize: "1rem",
                            fontWeight: "bold",
                          },
                          "& .MuiCardHeader-subheader": {
                            color: "#1e2466", // Ensures subheader is white
                            fontSize: "0.8rem",
                            letterSpacing: 0.5,
                            fontWeight: "bold",
                          },
                          textTransform: "capitalize",
                          p: "6px 8px",
                        }}
                      />

                      <CardContent
                        sx={{
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          pb: "6px !important",
                        }}
                      >
                        <Box>
                          <Typography variant="body2">
                            <strong>Previous Balance:</strong>{" "}
                            <span
                              style={{
                                color: statement.previousBalance < 0 && "red",
                              }}
                            >
                              {" "}
                              {statement.previousBalance.toFixed(2)}
                            </span>
                          </Typography>
                          <Typography variant="body2" sx={{ my: 1 }}>
                            <strong>Amount:</strong>{" "}
                            <span
                              style={{
                                color: getStatementAmountColor(statement),
                                fontWeight: "bold",
                              }}
                            >
                              {formatStatementAmount(statement)}
                            </span>
                          </Typography>

                          <Typography variant="body2">
                            <strong>Net Balance:</strong>{" "}
                            <span
                              style={{
                                color: statement.netBalance < 0 && "red",
                              }}
                            >
                              {" "}
                              {statement.netBalance.toFixed(2)}
                            </span>
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2">
                            {/* <strong>Status:</strong>{" "} */}
                            <span
                              style={{
                                color: color,
                                fontWeight: "bold",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              {icon}
                              <span style={{ marginLeft: "5px" }}>
                                {statement.status}
                              </span>
                            </span>
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
            </Box>
          ) : (
            // Desktop view using table
            <TableContainer>
              <Table aria-label="wallet statements table" sx={{ mt: 2 }}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="left">Type</StyledTableCell>
                    <StyledTableCell align="left">
                      Previous Balance
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      Amount <br />
                      (Credit / Debit)
                    </StyledTableCell>
                    <StyledTableCell align="left">Net Balance</StyledTableCell>
                    <StyledTableCell align="left">
                      <TableSortLabel
                        active={true}
                        direction={sortOrder}
                        onClick={handleSortRequest}
                        sx={{ color: "#1e2466 !important" }}
                      >
                        Date & Time
                      </TableSortLabel>
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      Transaction ID
                    </StyledTableCell>
                    <StyledTableCell align="left">Status</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayData.length === 0 && (
                    <StyledTableRow>
                      <StyledTableCell
                        colSpan={7}
                        align="center"
                        sx={{
                          color: "red",
                          fontWeight: "bold",
                          background: "#fff !important",
                          fontSize: "18px",
                        }}
                      >
                        Report Not Found .. 😔
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                  {displayData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((statement) => {
                      const { icon, color } = getStatusIcon(statement.status);
                      return (
                        <StyledTableRow key={statement._id}>
                          <StyledTableCell align="left">
                            <Chip
                              size="small"
                              label={getStatementDisplayType(statement)}
                              sx={getTypeStyles(statement.type)}
                            />
                          </StyledTableCell>
                          <StyledTableCell
                            align="left"
                            sx={{
                              color: statement.previousBalance < 0 && "red",
                            }}
                          >
                            {statement.previousBalance.toFixed(2)}
                          </StyledTableCell>
                          <StyledTableCell
                            align="left"
                            sx={{
                              color: getStatementAmountColor(statement),
                            }}
                          >
                            {formatStatementAmount(statement)}
                          </StyledTableCell>

                          <StyledTableCell
                            align="left"
                            sx={{
                              color: statement.netBalance < 0 && "red",
                            }}
                          >
                            {statement.netBalance.toFixed(2)}
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            {new Date(statement.createdAt).toLocaleString(
                              "en-GB",
                            )}
                          </StyledTableCell>
                          <StyledTableCell
                            align="left"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {statement.transactionId}{" "}
                            <IconButton
                              onClick={() =>
                                handleCopyTransactionId(statement.transactionId)
                              }
                              aria-label="copy transaction id"
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </StyledTableCell>
                          <StyledTableCell align="left">
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "start",
                                color: color,
                                fontWeight: "bold",
                                textTransform: "capitalize",
                              }}
                            >
                              {icon}
                              <span style={{ marginLeft: "5px" }}>
                                {statement.status}
                              </span>
                            </span>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[50, 100, 200, 300]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default WalletStatements;
